import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { TextInput } from '../../shared/components/TextInput';
import { useCheckIfEmailExists } from '../../shared/services/api/user';
import { cx } from '@emotion/css';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../shared/components/Button';
import { CheckEmailParams } from '../../shared/types/input-param-types';
import { h1Style } from '../../shared/styles/text';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Layout } from '../../shared/components/layout/Layout';
import { useNavigator } from '../../routing/navigator';
import { authenticationStore } from '../../shared/stores/authentification-store';
import { observer } from 'mobx-react';
import {
  authButtonContainerStyle,
  authFormContentStyle,
  authHeaderStyle,
  authSubtitleStyle,
} from '../../shared/styles/auth-layout';
import { useState } from 'react';
import { showPublicToast } from '../../shared/utils/show-public-toast';

const validationSchema = yup.object().shape({
  email: yup.string().email().required(),
});

export const CheckEmailPage = observer((): JSX.Element => {
  const checkIfEmailExists = useCheckIfEmailExists();
  const navigator = useNavigator();
  const authData = authenticationStore.getAuthenticationData();
  const [checking, setChecking] = useState(false);

  const methods = useForm<CheckEmailParams>({
    defaultValues: {
      email: authData?.email || '',
    },
    resolver: yupResolver(validationSchema),
    mode: 'all',
  });

  /**
   * Check if the email already exists within the database.
   * @param data
   */
  const handleCheckIfEmailExists: SubmitHandler<CheckEmailParams> = async (
    data: CheckEmailParams,
  ) => {
    try {
      setChecking(true);
      const { email } = data;
      authenticationStore.setAuthenticationData({
        ...authData,
        email,
      });
      const userExists = await checkIfEmailExists({ email });
      if (userExists) {
        navigator.toEmailSignIn();
      } else {
        navigator.toSignUp();
      }
    } catch (error: any) {
      console.error(error);
      showPublicToast(`We couldn't check that email. Please try again.`);
    } finally {
      setChecking(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleCheckIfEmailExists)}>
        <Layout.Content className={authFormContentStyle}>
          <div>
            <h1 className={cx(h1Style, authHeaderStyle)}>Start now.</h1>
            <p className={authSubtitleStyle}>
              Enter your email to sign in or create an account.
            </p>
            <TextInput
              name={'email'}
              placeHolder={'enter your email'}
              type={'email'}
            />
          </div>
          <div className={authButtonContainerStyle}>
            <Button
              variant={ButtonVariantEnum.TEXT}
              size={ButtonSizeEnum.LARGE}
              text={'back'}
              onClick={navigator.toLanding}
            />
            <Button
              variant={ButtonVariantEnum.FILLED}
              size={ButtonSizeEnum.LARGE}
              text={'next'}
              type={'submit'}
              loading={checking}
            />
          </div>
        </Layout.Content>
      </form>
    </FormProvider>
  );
});
